import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useChemistryStore } from '../../stores/chemistryStore';
import { useNavigationStore } from '../../stores/navigationStore';
import { useUIStore } from '../../stores/uiStore';
import { useTaskStore } from '../../stores/taskStore';
import FileList from '../../components/chemistry/FileList';
import MergeDialog from '../../components/chemistry/MergeDialog';
import { StructureEditorModal } from '../../components/chemistry/StructureEditor';
import { ChemFile, Project } from '../../types/chemistry';
import { chemistryApi } from '../../api/chemistry';
import PageHeader from '../../components/common/PageHeader';
import PageTransition from '../../components/common/PageTransition';
import DedupDialog from '../../components/chemistry/DedupDialog';
import apiClient from '../../api/client';
import ProjectFilesTab from '../../components/documents/ProjectFilesTab';
import DocumentDetailPanel from '../../components/documents/DocumentDetailPanel';
import { documentsApi, Document as DocRepoDocument } from '../../api/documents';
import ProjectReactionsTab from '../../components/reactions/ProjectReactionsTab';
import ProjectPatentsTab from '../../components/patents/ProjectPatentsTab';
import ProjectCompoundsTab from '../../components/chemistry/ProjectCompoundsTab';
import ProjectTargetsTab from '../../components/chemistry/ProjectTargetsTab';
import ProjectADMETTab from '../../components/chemistry/ProjectADMETTab';
import ProjectScreensTab from '../../components/chemistry/ProjectScreensTab';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import { useFeatureStore } from '../../stores/featureStore';
import AmplitudeEvaluationPage from './AmplitudeEvaluationPage';

type SearchType = 'substructure' | 'similarity' | 'exact';

// Drop zone component for dragging files to projects
interface ProjectDropZoneProps {
  project: Project;
  onDrop: (fileIds: string[]) => void;
}

const ProjectDropZone: React.FC<ProjectDropZoneProps> = ({ project, onDrop }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'files' && data.fileIds?.length > 0) {
        onDrop(data.fileIds);
      }
    } catch (err) {
      console.error('Drop parse error:', err);
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed transition-all ${
        isDragOver
          ? 'border-emerald-400 bg-emerald-50 scale-105'
          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-gray-50 dark:bg-gray-800'
      }`}
    >
      <div
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: project.color }}
      />
      <span className={`text-sm font-medium truncate ${isDragOver ? 'text-emerald-700' : 'text-gray-600 dark:text-gray-400'}`}>
        {project.name}
      </span>
      {isDragOver && (
        <svg className="w-4 h-4 text-emerald-500 ml-auto flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  );
};

type ProjectTab = 'compounds' | 'targets' | 'reactions' | 'documents' | 'patents' | 'admet' | 'screens';

const FilesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const projectId = searchParams.get('project') ? parseInt(searchParams.get('project')!, 10) : null;
  const rawTab = searchParams.get('tab');
  // Support legacy tab=proteins bookmarks by redirecting to tab=targets
  const normalizedTab = rawTab === 'proteins' ? 'targets' : rawTab;
  const activeTab: ProjectTab = (normalizedTab === 'targets' || normalizedTab === 'documents' || normalizedTab === 'reactions' || normalizedTab === 'patents' || normalizedTab === 'admet' || normalizedTab === 'screens') ? normalizedTab : 'compounds';

  // Feature flags
  const { fetchFeatures, hasFeature } = useFeatureStore();
  const ipIntelligenceEnabled = hasFeature('ip_intelligence');
  useEffect(() => { fetchFeatures(); }, [fetchFeatures]);
  const participantSearchHandledRef = useRef(false);

  // Document detail panel state
  const [selectedDocument, setSelectedDocument] = useState<DocRepoDocument | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [detailPanelInitialTab, setDetailPanelInitialTab] = useState<'details' | 'versions' | 'links' | 'audit'>('details');
  const [filesTabFolderNav, setFilesTabFolderNav] = useState<string | null>(null);
  const [repositoryDocumentCount, setRepositoryDocumentCount] = useState(0);

  // Lookup: original_filename (lowercase) → chemistry data file ID for "Open in Grid"
  const [dataFileMap, setDataFileMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (projectId) {
      chemistryApi.getFiles(projectId).then((files) => {
        const map = new Map<string, string>();
        for (const f of files) {
          const name = (f.originalFileName || f.fileName).replace(/^[a-f0-9]{8}_/i, '').toLowerCase();
          map.set(name, f.id);
        }
        setDataFileMap(map);
      }).catch(() => {});
      documentsApi.listDocuments(projectId, { limit: 200 }).then((documents) => {
        setRepositoryDocumentCount(documents.length);
      }).catch(() => setRepositoryDocumentCount(0));
    } else {
      setDataFileMap(new Map());
      setRepositoryDocumentCount(0);
    }
  }, [projectId]);

  const handleDocumentSelect = useCallback((doc: DocRepoDocument) => {
    setSelectedDocument(doc);
    setDetailPanelOpen(true);
    setDetailPanelInitialTab('details');
  }, []);

  const handleNavigateToEditor = useCallback((fileId: string) => {
    navigate(`/chemistry/file/${fileId}/editor`);
  }, [navigate]);

  const {
    selectedFileIds,
    lockedFileIds,
    fetchLockedFiles,
    clearSelection,
    setSelectedFileIds,
    setFiles: setStoreFiles,  // Sync files to store for MergeDialog
    // Cross-file search (persisted in store)
    fileSearchQuery,
    fileSearchType,
    fileSearchResponse,
    setFileSearchResults,
    clearFileSearch,
  } = useChemistryStore();

  // Local files state (filtered by project)
  const [files, setFiles] = useState<ChemFile[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [filesError, setFilesError] = useState<string | null>(null);

  // Project state
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const isAmplitudeRadiantEvaluation =
    window.location.hostname === 'amplitude.adroitai.ai' &&
    currentProject?.name === 'Radiant Biotherapeutics — Public Evaluation';

  const { isAnyRunning, tasks } = useTaskStore();

  const {
    isMergeDialogOpen,
    isDedupDialogOpen,
    dedupFileId,
    openUploadDialog,
    openMergeDialog,
    closeMergeDialog,
    closeDedupDialog,
  } = useUIStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [drawnStructure, setDrawnStructure] = useState<{ molfile: string; smiles: string } | null>(null);
  const [structureImage, setStructureImage] = useState<string | null>(null);

  // Structure search state (transient - not persisted)
  const [searchType, setSearchType] = useState<SearchType>(fileSearchType || 'substructure');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [similarityThreshold, setSimilarityThreshold] = useState(0.7);

  // Chemistry search options
  const [stereoMode, setStereoMode] = useState<'strict' | 'ignore'>('strict');
  const [tautomerMode, setTautomerMode] = useState<'exact' | 'inchi'>('exact');

  // Sidebar collapse state
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  // Bulk move state
  const [projects, setProjects] = useState<Project[]>([]);
  const [isMoveDropdownOpen, setIsMoveDropdownOpen] = useState(false);
  const [isMovingFiles, setIsMovingFiles] = useState(false);

  // Fetch projects for move dropdown
  useEffect(() => {
    chemistryApi.getProjects().then(setProjects).catch(console.error);
  }, []);

  // Detect ?view=shared or ?view=library
  const viewParam = searchParams.get('view');
  const isSharedView = viewParam === 'shared';
  const isLibraryView = viewParam === 'library';

  // Fetch files (optionally filtered by project, shared-with-me, or reference library)
  const fetchFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    setFilesError(null);
    try {
      if (isLibraryView) {
        // Fetch approved reference library files — gracefully empty if endpoint fails
        let libraryFiles: any[] = [];
        try {
          const res = await apiClient.get('/v1/reference-library');
          libraryFiles = (res.data || []).map((entry: any) => ({
            id: String(entry.file_id),
            fileName: entry.filename,
            originalFileName: entry.filename,
            fileSize: 0,
            moleculesLibraryCount: entry.molecule_count || 0,
            moleculesInvalid: 0,
            propertiesCount: 0,
            status: 'completed',
            createdDate: entry.pushed_at || '',
            modifiedDate: null,
            isReadOnly: entry.is_locked ?? true,
            sharedBy: entry.pushed_by_name,
          }));
        } catch {
          // Reference Library endpoint may be unavailable — show empty list
        }
        setFiles(libraryFiles);
        setStoreFiles(libraryFiles);
      } else if (isSharedView) {
        // Fetch files shared with the current user
        const res = await apiClient.get('/v1/files/shared-with-me');
        const shared = (res.data || []).map((s: any) => ({
          id: String(s.file_id),
          fileName: s.filename,
          originalFileName: s.filename,
          fileSize: 0,
          moleculesLibraryCount: 0,
          moleculesInvalid: 0,
          propertiesCount: 0,
          status: 'completed',
          createdDate: s.shared_at || '',
          modifiedDate: null,
          isReadOnly: s.permission === 'view',
          sharedBy: s.shared_by_name || s.shared_by_email,
          permission: s.permission,
        }));
        setFiles(shared);
        setStoreFiles(shared);
      } else {
        const data = await chemistryApi.getFiles(projectId || undefined);
        setFiles(data);
        setStoreFiles(data);  // Sync to store for MergeDialog
      }
    } catch (err: any) {
      setFilesError(err.message || 'Failed to fetch files');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [projectId, isSharedView, isLibraryView, setStoreFiles]);

  // Bulk move files handler
  const handleBulkMove = useCallback(async (targetProjectId: number) => {
    if (selectedFileIds.length === 0) return;
    setIsMovingFiles(true);
    try {
      await chemistryApi.bulkMoveFiles(selectedFileIds, targetProjectId);
      setIsMoveDropdownOpen(false);
      clearSelection();
      await fetchFiles();
    } catch (err: any) {
      setFilesError(err.message || 'Failed to move files');
    } finally {
      setIsMovingFiles(false);
    }
  }, [selectedFileIds, clearSelection, fetchFiles]);

  useEffect(() => {
    fetchFiles();
    fetchLockedFiles();
  }, [fetchFiles, fetchLockedFiles]);

  // Fetch project details if projectId is provided
  // Include location.key to refetch when navigating back (ensures fresh data after rename)
  useEffect(() => {
    if (projectId) {
      chemistryApi.getProject(projectId)
        .then(setCurrentProject)
        .catch(() => setCurrentProject(null));
    } else {
      setCurrentProject(null);
    }
  }, [projectId, location.key]);

  // Sync project context to navigation store for breadcrumbs
  useEffect(() => {
    if (currentProject) {
      useNavigationStore.getState().setProject(currentProject.id, currentProject.name);
      // Clear file/molecule context when viewing project-level page
      useNavigationStore.getState().setFile(null, null);
      useNavigationStore.getState().setMolecule(null, null);
    }
  }, [currentProject]);

  // Re-fetch locked files when tasks change (start/complete)
  useEffect(() => {
    fetchLockedFiles();
  }, [tasks, fetchLockedFiles]);

  // Handle incoming participant search from ReactionGrid
  useEffect(() => {
    const state = location.state as { participantSearch?: string; searchType?: SearchType } | null;
    if (state?.participantSearch && !participantSearchHandledRef.current) {
      participantSearchHandledRef.current = true;
      console.log('[FilesPage] Incoming participant search:', state.participantSearch);

      // Set the drawn structure to trigger the search
      setDrawnStructure({ molfile: '', smiles: state.participantSearch });
      if (state.searchType) {
        setSearchType(state.searchType);
      }

      // Clear the location state to prevent re-triggering
      navigate(location.pathname + location.search, { replace: true, state: null });

      // Trigger the search after a short delay to allow state to settle
      setTimeout(async () => {
        setIsSearching(true);
        try {
          const smiles = encodeURIComponent(state.participantSearch!);
          const url = `/search/substructure?smarts=${smiles}`;
          const response = await apiClient.get(url);
          const data = response.data;
          setFileSearchResults(state.participantSearch!, state.searchType || 'substructure', data);
        } catch (e) {
          console.error('[FilesPage] Participant search failed:', e);
          setSearchError(e instanceof Error ? e.message : 'Search failed');
        } finally {
          setIsSearching(false);
        }
      }, 100);
    }
  }, [location.state, location.pathname, location.search, navigate, setFileSearchResults]);

  // Reset participant search flag when location changes
  useEffect(() => {
    participantSearchHandledRef.current = false;
  }, [location.key]);

  // Strip UUID prefix from filename for display/search
  const cleanFileName = (name: string) => name.replace(/^[a-f0-9]{8}_/i, '');

  const filteredFiles = files.filter((file) => {
    const name = cleanFileName(file.originalFileName || file.fileName);
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Check if any selected file is a reaction file (can't merge reactions)
  const hasReactionFileSelected = selectedFileIds.some((id) => {
    const file = files.find((f) => f.id === id);
    if (!file) return false;
    // Check fileType or file extension
    if (file.fileType === 'reactions') return true;
    const ext = (file.originalFileName || file.fileName).toLowerCase().split('.').pop();
    return ext === 'rxn' || ext === 'rdf';
  });

  const handleDelete = async () => {
    if (selectedFileIds.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedFileIds.length} file(s)?`)) {
      try {
        await chemistryApi.deleteFiles(selectedFileIds);
        setSelectedFileIds([]);
        await fetchFiles();
      } catch (err: any) {
        const message = err?.response?.data?.detail
          || 'Something went wrong while deleting the files. Refresh the page and try again.';
        setFilesError(message);
        setSelectedFileIds([]);
        await fetchFiles();
      }
    }
  };

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);

  const handleDownload = async (format: string) => {
    if (selectedFileIds.length === 0) return;

    setIsDownloading(true);
    setIsDownloadDropdownOpen(false);
    try {
      for (const fileId of selectedFileIds) {
        const file = files.find(f => f.id === fileId);
        if (!file) continue;

        const response = await apiClient.post('/export/molecules', {
          file_id: Number(fileId),
          molecule_ids: [],
          format,
        }, { responseType: 'blob' });

        // Build filename from original name
        const baseName = (file.originalFileName || file.fileName).replace(/^[a-f0-9]{8}_/i, '').replace(/\.[^.]+$/, '');
        const extMap: Record<string, string> = { sdf: '.sdf.gz', csv: '.csv', smi: '.smi' };
        const filename = baseName + (extMap[format] || `.${format}`);

        const blob = new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleMergeComplete = () => {
    closeMergeDialog();
    fetchFiles();
    clearSelection();
  };

  const handleStructureSave = async (molfile: string, smiles: string) => {
    console.log('Saving structure, SMILES:', smiles);
    setDrawnStructure({ molfile, smiles });

    // Get SVG rendering from backend
    try {
      console.log('Fetching SVG...');
      const response = await fetch('/api/v2/indigo/render', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          structure: { value: smiles || molfile, type: smiles ? 'RAW' : 'MOL' },
          width: '200px',
          height: '120px'
        })
      });
      console.log('Response status:', response.status);
      if (response.ok) {
        const svg = await response.text();
        console.log('SVG length:', svg.length);
        setStructureImage(svg);
      }
    } catch (e) {
      console.error('Render failed:', e);
    }
  };

  const handleStructureSearch = async () => {
    if (!drawnStructure?.smiles) {
      setSearchError('Please draw a structure first');
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      let url: string;
      const smiles = encodeURIComponent(drawnStructure.smiles);
      // Add chemistry options to all search types
      const stereoParam = `&stereo_mode=${stereoMode}`;
      const tautomerParam = `&tautomer_mode=${tautomerMode}`;

      switch (searchType) {
        case 'substructure':
          url = `/search/substructure?smarts=${smiles}${stereoParam}${tautomerParam}`;
          break;
        case 'similarity':
          url = `/search/similarity?smiles=${smiles}&threshold=${similarityThreshold}`;
          break;
        case 'exact':
          url = `/search/exact?smiles=${smiles}${stereoParam}`;
          break;
      }

      const response = await apiClient.get(url);
      const data = response.data;
      // Save to store for persistence across navigation
      setFileSearchResults(drawnStructure.smiles, searchType, data);
    } catch (e: any) {
      console.error('Search error:', e);
      const msg = e?.response?.data?.detail || e?.message || 'Search failed';
      setSearchError(msg);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    clearFileSearch();
    setSearchError(null);
  };

  // Convert search results to ChemFile format for FileList display
  const searchResultsAsFiles: ChemFile[] = fileSearchResponse?.results.map(r => ({
    id: String(r.id),
    fileName: r.fileName,
    originalFileName: r.fileName,
    fileSize: 0,
    moleculesLibraryCount: r.totalMolecules,
    moleculesInvalid: 0,
    propertiesCount: 0,
    status: 'completed',
    createdDate: r.createdAt || '',
    modifiedDate: r.modifiedAt || null,
    isReadOnly: false,
    matchCount: r.matchCount,  // Extra field for search results
  })) || [];

  return (
    <PageTransition>
    <div className="flex flex-col h-full min-h-screen" style={{ background: 'var(--content-bg)' }}>
      <PageHeader
        title={isLibraryView ? 'Reference Library' : isSharedView ? 'Shared with Me' : currentProject ? currentProject.name : 'All Files'}
        subtitle={isLibraryView ? 'Approved reference library files shared across the organisation' : isSharedView ? 'Files that others have shared with you' : currentProject?.description || 'Manage and search your chemical structure libraries'}
        breadcrumbs={[
          { label: 'Projects', to: '/chemistry/projects' },
          ...(currentProject ? [{ label: currentProject.name }] : []),
        ]}
      />

      {/* Project View — with tab navigation */}
      {projectId && !isSharedView ? (
        <>
          {/* In-project tab bar */}
          <div className="max-w-7xl mx-auto w-full px-6 pt-4">
            <div className="flex gap-1 bg-white dark:bg-gray-900 rounded-xl shadow-sm p-1 w-fit">
              {([
                { id: 'compounds' as ProjectTab, label: isAmplitudeRadiantEvaluation ? 'Evaluation' : 'Compounds', count: isAmplitudeRadiantEvaluation ? undefined : currentProject?.contentCounts?.molecules },
                { id: 'targets' as ProjectTab, label: 'Targets', count: currentProject?.contentCounts?.proteins },
                { id: 'reactions' as ProjectTab, label: 'Reactions', count: currentProject?.contentCounts?.reactions },
                { id: 'documents' as ProjectTab, label: 'Files', count: (currentProject?.contentCounts?.files || 0) + repositoryDocumentCount },
                { id: 'screens' as ProjectTab, label: 'Screens' },
                { id: 'admet' as ProjectTab, label: 'ADMET' },
                { id: 'patents' as ProjectTab, label: 'Patents', count: currentProject?.patentRiskCount, gated: !ipIntelligenceEnabled },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.gated) return; // Disabled tabs don't navigate
                    const params = new URLSearchParams();
                    params.set('project', String(projectId));
                    if (tab.id !== 'compounds') params.set('tab', tab.id);
                    navigate(`/chemistry/files?${params.toString()}`);
                  }}
                  title={tab.gated ? 'IP Intelligence is a premium feature. Contact sales@adroitai.ai to enable patent analysis.' : undefined}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                    tab.gated
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
                      : activeTab === tab.id
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {tab.gated && (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  )}
                  {tab.label}
                  {tab.count != null && tab.count > 0 && !tab.gated && (
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                      activeTab === tab.id
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}>
                      {tab.count.toLocaleString()}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          {activeTab === 'compounds' ? (
            isAmplitudeRadiantEvaluation ? (
              <AmplitudeEvaluationPage projectId={projectId} />
            ) : (
              <ProjectCompoundsTab projectId={projectId} />
            )
          ) : activeTab === 'targets' ? (
            <ErrorBoundary fallbackTitle="Targets unavailable">
              <ProjectTargetsTab projectId={projectId} />
            </ErrorBoundary>
          ) : activeTab === 'documents' ? (
            <div className="flex flex-1 overflow-hidden max-w-7xl mx-auto w-full">
              <div className="flex-1 flex flex-col p-6 min-w-0">
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 flex-1 flex flex-col min-h-0" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                  <ProjectFilesTab
                    projectId={projectId}
                    onDocumentSelect={handleDocumentSelect}
                    onNavigateToEditor={handleNavigateToEditor}
                    navigateToFolder={filesTabFolderNav}
                  />
                </div>
              </div>
              <DocumentDetailPanel
                document={selectedDocument}
                isOpen={detailPanelOpen}
                onClose={() => setDetailPanelOpen(false)}
                onRefresh={() => {}}
                initialTab={detailPanelInitialTab}
                dataFileId={selectedDocument ? (dataFileMap.get(selectedDocument.original_filename.toLowerCase()) || null) : null}
                onNavigateToEditor={handleNavigateToEditor}
                onFolderNavigate={(folderId) => {
                  setFilesTabFolderNav(folderId);
                  // Reset after a tick so the same folder can be clicked again
                  setTimeout(() => setFilesTabFolderNav(null), 100);
                }}
              />
            </div>
          ) : activeTab === 'reactions' ? (
            <ProjectReactionsTab projectId={projectId} />
          ) : activeTab === 'patents' ? (
            ipIntelligenceEnabled ? (
              <ProjectPatentsTab projectId={projectId} />
            ) : (
              <div className="flex items-center justify-center py-32">
                <div className="text-center max-w-md">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">IP Intelligence is a Premium Feature</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Patent collision analysis, freedom-to-operate assessment, and IP landscape mapping require a premium licence.
                  </p>
                  <a
                    href="mailto:sales@adroitai.ai"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded-lg transition-colors text-sm"
                  >
                    Contact sales@adroitai.ai
                  </a>
                </div>
              </div>
            )
          ) : activeTab === 'admet' ? (
            <ProjectADMETTab projectId={projectId} />
          ) : activeTab === 'screens' ? (
            <ProjectScreensTab projectId={projectId} />
          ) : null}
        </>
      ) : (
      /* All-files / shared / search results view */
      <div className="flex flex-1 overflow-hidden p-6 gap-6 max-w-7xl mx-auto w-full">
        {/* Sidebar - Collapsible */}
        <div
          className={`flex-shrink-0 overflow-y-auto transition-all duration-200 ${sidebarExpanded ? 'w-80' : 'w-[60px]'}`}
          style={{ maxHeight: 'calc(100vh - 180px)' }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl h-full">
            {/* Collapsed state - icon buttons */}
            {!sidebarExpanded && (
              <div className="flex flex-col items-center gap-3 py-4">
                <button
                  onClick={() => { setSidebarExpanded(true); setIsEditorOpen(true); }}
                  className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-200"
                  title="Draw Structure"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
                <button
                  onClick={() => setSidebarExpanded(true)}
                  className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-700 hover:scale-110 transition-all duration-200"
                  title="Search Tools"
                >
                  <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>
                {drawnStructure && (
                  <div className="w-3 h-3 bg-emerald-500 rounded-full" title="Structure query active" />
                )}
                {fileSearchResponse !== null && (
                  <div className="text-xs font-bold text-emerald-600 text-center leading-tight">
                    {fileSearchResponse.totalMatches}
                  </div>
                )}
              </div>
            )}

            {/* Expanded state - full content */}
            {sidebarExpanded && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Search Tools</h2>
                  <button
                    onClick={() => setSidebarExpanded(false)}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-400 transition-colors"
                    title="Collapse panel"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                </div>

                {/* Draw Structure - Prominent Action Card */}
                <button
                  onClick={() => setIsEditorOpen(true)}
                  className="w-full p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl hover:from-emerald-100 hover:to-teal-100 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Draw Structure</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Open molecular editor</p>
                    </div>
                  </div>
                </button>

            {/* Drawn Structure Preview + Search (immediately after Draw button) */}
            {drawnStructure && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">Query</h3>
                  <button
                    onClick={() => {
                      setDrawnStructure(null);
                      setStructureImage(null);
                    }}
                    className="text-gray-400 hover:text-red-500 text-xs font-medium px-2 py-1 rounded hover:bg-red-50 transition-all"
                  >
                    Clear
                  </button>
                </div>

                {/* Structure Image - compact */}
                <div className="relative group mb-4">
                  {structureImage ? (
                    <div
                      className="w-full flex items-center justify-center bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                      style={{ height: '120px', maxHeight: '120px' }}
                      dangerouslySetInnerHTML={{ __html: structureImage }}
                    />
                  ) : (
                    <div className="w-full flex items-center justify-center bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700" style={{ height: '120px' }}>
                      <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <button
                    onClick={() => setIsEditorOpen(true)}
                    className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <span className="bg-white dark:bg-gray-900 px-2 py-1 rounded text-xs font-medium text-gray-700 dark:text-gray-300 shadow">Edit</span>
                  </button>
                </div>

                {/* Search Type */}
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value as SearchType)}
                  className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium text-gray-700 dark:text-gray-300 mb-3"
                >
                  <option value="substructure">Substructure Search</option>
                  <option value="similarity">Similarity Search</option>
                  <option value="exact">Exact Match</option>
                </select>

                {searchType === 'similarity' && (
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                      Similarity: <span className="text-emerald-600 font-medium">{(similarityThreshold * 100).toFixed(0)}%</span>
                    </label>
                    <input
                      type="range"
                      min="0.5"
                      max="1"
                      step="0.05"
                      value={similarityThreshold}
                      onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
                      className="w-full accent-emerald-500 h-2"
                    />
                  </div>
                )}

                {/* Chemistry Options */}
                <div className="mb-3 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Chemistry Options</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Stereochemistry</label>
                      <div className="space-y-1">
                        <div className="relative group/strict">
                          <button
                            onClick={() => setStereoMode('strict')}
                            className={`w-full px-2 py-1.5 text-sm text-left rounded border transition-colors ${
                              stereoMode === 'strict'
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            Strict (R/S must match)
                          </button>
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/strict:opacity-100 group-hover/strict:delay-500">
                            Only find molecules with exactly the same 3D arrangement. (R)-Lactic acid will NOT match (S)-Lactic acid. Use when chirality matters for biological activity.
                          </div>
                        </div>
                        <div className="relative group/ignore">
                          <button
                            onClick={() => setStereoMode('ignore')}
                            className={`w-full px-2 py-1.5 text-sm text-left rounded border transition-colors ${
                              stereoMode === 'ignore'
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            Ignore stereochemistry
                          </button>
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/ignore:opacity-100 group-hover/ignore:delay-500">
                            Find all molecules with the same connectivity, regardless of 3D arrangement. (R)-Lactic acid WILL match (S)-Lactic acid. Use for scaffold searches.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Tautomers</label>
                      <div className="space-y-1">
                        <div className="relative group/exact">
                          <button
                            onClick={() => setTautomerMode('exact')}
                            className={`w-full px-2 py-1.5 text-sm text-left rounded border transition-colors ${
                              tautomerMode === 'exact'
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            Exact (keto ≠ enol)
                          </button>
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/exact:opacity-100 group-hover/exact:delay-500">
                            Treat tautomers as different molecules. Acetylacetone keto form will NOT match enol form. Use when the specific form matters.
                          </div>
                        </div>
                        <div className="relative group/inchi">
                          <button
                            onClick={() => setTautomerMode('inchi')}
                            className={`w-full px-2 py-1.5 text-sm text-left rounded border transition-colors ${
                              tautomerMode === 'inchi'
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-800 font-medium'
                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                          >
                            Match tautomers (InChI)
                          </button>
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/inchi:opacity-100 group-hover/inchi:delay-500">
                            Treat tautomers as equivalent. Acetylacetone keto form WILL match enol form. Use when searching for a compound that may exist in multiple forms.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search Button - always visible */}
                <button
                  onClick={handleStructureSearch}
                  disabled={isSearching || !drawnStructure?.smiles}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold rounded-lg hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
                >
                  {isSearching ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Searching...
                    </span>
                  ) : (
                    'Search All Files'
                  )}
                </button>

                {searchError && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-lg flex items-start justify-between gap-2">
                    <p className="text-xs text-red-600">{searchError}</p>
                    <button
                      onClick={() => setSearchError(null)}
                      className="text-red-400 hover:text-red-600 flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Search Results Summary */}
            {fileSearchResponse !== null && (
              <div className="mt-4 p-4 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-100">Results</span>
                  <button
                    onClick={clearSearch}
                    className="text-emerald-100 hover:text-white text-xs font-medium hover:bg-white/20 dark:hover:bg-gray-300/20 px-2 py-1 rounded transition-all"
                  >
                    Clear
                  </button>
                </div>
                <p className="text-2xl font-bold">{fileSearchResponse.totalMatches}</p>
                <p className="text-emerald-100 text-sm">matches in {fileSearchResponse.totalFiles} files</p>
              </div>
            )}

            {/* Upload MOL File */}
            <button className="w-full mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 transition-all text-left flex items-center gap-3">
              <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300 text-sm">Upload .mol file</span>
                <p className="text-xs text-gray-400">Import structure</p>
              </div>
            </button>

                {/* Quick Move - at bottom */}
                {projects.filter(p => p.id !== projectId).length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">
                      Quick Move
                    </h3>
                    <div className="space-y-1.5">
                      {projects.filter(p => p.id !== projectId).map((project) => (
                        <ProjectDropZone
                          key={project.id}
                          project={project}
                          onDrop={(fileIds) => handleBulkMove(project.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - File List Card */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Toolbar */}
          <div className="bg-white dark:bg-gray-900 rounded-t-2xl px-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">{files.length}</span>
                <span className="ml-1">files</span>
              </span>
              {selectedFileIds.length > 0 && (
                <>
                  <div className="w-px h-6 bg-gray-200 dark:bg-gray-700"></div>
                  <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                    {selectedFileIds.length} selected
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={openMergeDialog}
                      disabled={selectedFileIds.length < 2 || isAnyRunning || hasReactionFileSelected}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium"
                      title={hasReactionFileSelected ? 'Merge not available for reaction files' : undefined}
                    >
                      {isAnyRunning ? 'Merging...' : 'Merge'}
                    </button>

                    {/* Move to dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setIsMoveDropdownOpen(!isMoveDropdownOpen)}
                        disabled={isMovingFiles}
                        className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium flex items-center gap-1"
                      >
                        {isMovingFiles ? (
                          <>
                            <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                            Moving...
                          </>
                        ) : (
                          <>
                            Move to
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                      {isMoveDropdownOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setIsMoveDropdownOpen(false)}
                          />
                          <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 py-2 min-w-[180px]">
                            {projects.filter(p => p.id !== projectId).length === 0 ? (
                              <div className="px-4 py-3 text-sm text-gray-400">No other projects</div>
                            ) : (
                              projects.filter(p => p.id !== projectId).map((project) => (
                                <button
                                  key={project.id}
                                  onClick={() => handleBulkMove(project.id)}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2"
                                >
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: project.color }}
                                  />
                                  {project.name}
                                </button>
                              ))
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Download dropdown */}
                    {selectedFileIds.length > 0 && (
                      <div className="relative">
                        <button
                          onClick={() => setIsDownloadDropdownOpen(!isDownloadDropdownOpen)}
                          disabled={isDownloading}
                          className="text-sm text-gray-600 dark:text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 px-3 py-2 rounded-lg disabled:text-gray-400 disabled:cursor-not-allowed transition-all font-medium flex items-center gap-1"
                        >
                          {isDownloading ? (
                            <>
                              <div className="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                              Download
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                        {isDownloadDropdownOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setIsDownloadDropdownOpen(false)}
                            />
                            <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 py-2 min-w-[200px]">
                              <button
                                onClick={() => handleDownload('sdf')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                              >
                                <span>SDF (Structure-Data)</span>
                                <span className="text-xs text-gray-400">.sdf.gz</span>
                              </button>
                              <button
                                onClick={() => handleDownload('csv')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                              >
                                <span>CSV</span>
                                <span className="text-xs text-gray-400">.csv</span>
                              </button>
                              <button
                                onClick={() => handleDownload('smi')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-700 flex items-center justify-between"
                              >
                                <span>SMILES</span>
                                <span className="text-xs text-gray-400">.smi</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <button
                      onClick={handleDelete}
                      className="text-sm text-red-500 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-all font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 pr-4 py-2.5 w-72 text-sm bg-gray-50 dark:bg-gray-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white dark:focus:bg-gray-800 transition-all placeholder-gray-400"
              />
              <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* File List */}
          <div className="flex-1 bg-white dark:bg-gray-900 rounded-b-2xl shadow-xl overflow-hidden">
            {fileSearchResponse !== null ? (
              fileSearchResponse.results.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-20">
                  <div className="w-24 h-24 mb-6 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No matches found</p>
                  <p className="text-gray-400 mt-2">Try a different structure or search type</p>
                  <button
                    onClick={clearSearch}
                    className="mt-6 px-6 py-2.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl transition-all"
                  >
                    Back to all files
                  </button>
                </div>
              ) : (
                <FileList
                  files={searchResultsAsFiles}
                  showMatchCount={true}
                  searchQuery={fileSearchQuery || undefined}
                  searchType={fileSearchType || undefined}
                  disabled={isAnyRunning}
                  lockedFileIds={lockedFileIds}
                  currentProjectId={projectId}
                  onFileMoved={fetchFiles}
                />
              )
            ) : isLoadingFiles ? (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <span className="text-gray-500 dark:text-gray-400 font-medium">Loading files...</span>
              </div>
            ) : filesError ? (
              <div className="flex items-center justify-center h-full py-20">
                <div className="text-red-500 bg-red-50 px-6 py-4 rounded-xl font-medium">{filesError}</div>
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="w-24 h-24 mb-6 bg-gradient-to-br from-emerald-100 to-teal-50 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No files in this project yet</p>
                <p className="text-gray-400 mt-2">Upload an SDF, CSV, or Excel file to get started.</p>
                <button
                  onClick={() => openUploadDialog(projectId ?? undefined)}
                  className="mt-6 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-semibold rounded-xl hover:from-teal-600 hover:to-emerald-600 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  Upload File
                </button>
              </div>
            ) : (
              <FileList
                  files={filteredFiles}
                  disabled={isAnyRunning}
                  lockedFileIds={lockedFileIds}
                  currentProjectId={projectId}
                  onFileMoved={fetchFiles}
                />
            )}
          </div>
        </div>
      </div>
      )}

      {/* Dialogs — UploadDialog replaced by UnifiedImportDialog in AppLayout */}
      <MergeDialog
        isOpen={isMergeDialogOpen}
        onClose={closeMergeDialog}
        onComplete={handleMergeComplete}
        projectId={projectId}
      />
      <StructureEditorModal
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
        onSave={handleStructureSave}
        initialStructure={drawnStructure?.molfile || drawnStructure?.smiles || ''}
        title={drawnStructure ? "Edit Structure" : "Draw Structure for Search"}
      />
      {isDedupDialogOpen && dedupFileId && (
        <DedupDialog
          isOpen={isDedupDialogOpen}
          onClose={closeDedupDialog}
          fileId={dedupFileId}
          fileName={files.find(f => f.id === dedupFileId)?.originalFileName || files.find(f => f.id === dedupFileId)?.fileName || 'File'}
          columns={[]}
          onComplete={fetchFiles}
        />
      )}
    </div>
    </PageTransition>
  );
};

export default FilesPage;
